import { memo, useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import { trackSalesPartnerFinderSelectPreferred } from "~/app/analytics/ga/affiliate";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, ChipSelect, Grid, type SelectChangeEvent } from "~/common/mui";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import GroupedSelect, {
  type GroupedSelectRef,
  type Suggestion,
} from "~/features/salesPartner/components/GroupedSelect/GroupedSelect";
import SPFinderFilters, {
  type SPFinderFiltersRef,
} from "~/features/salesPartner/components/SPFinderFilters/SPFinderFilters";
import classes from "./SalesPartnerSearchBox.module.less";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import Modal from "~/common/components/Modal";
import { RadiusSelectButtonGroup } from "~/features/salesPartner/components/SearchCard/GooglePlacesInput/RadiusSelectButtonGroup";
import { getCountryDetail, useGoogleAutocomplete } from "~/domains/salesPartner/api/getAutocomplete";
import { throttle } from "lodash";
import { getCompanyList } from "~/domains/salesPartner/api/getCompanyList";
import SearchIcon from "@mui/icons-material/Search";
import {
  buildSalesPartnerSearchTrackingValues,
  type SalesPartnerSearchFormValues,
  trackPartnerFinderSearch,
} from "~/app/analytics/ga/salesPartner";
import { SearchType, type SalesPartnerSearchQuery } from "~/domains/salesPartner/api/getSalesPartnerSearch";
import { fetchCurrentLocation } from "~/domains/salesPartner/api/fetchCurrentLocation";

export enum SearchTypesSP {
  Partner = "partner",
  Address = "address",
}

export interface Option {
  key: string;
  label: string;
}

export type SASearchFormFields = SalesPartnerSearchFormValues;

interface Props {
  query: SalesPartnerSearchQuery;
  markersLoading: boolean;
  onCommit: (query: SalesPartnerSearchQuery) => void;
  hasPreferredSalesPartner: boolean;
  productCompetencesOptions?: Option[];
  certifiedCompetencesOptions?: Option[];
  isCheckout?: boolean;
}

const emptySearchQuery = (query: SalesPartnerSearchQuery): SalesPartnerSearchQuery => ({
  ...query,
  query: "",
  radius: -1,
  searchType: SearchType.Address,
  productCompetence: [],
  certifiedCompetence: [],
  countryCode: "",
  lat: null,
  lng: null,
  preferredOnly: false,
});

const normalizeQuery = (query: SalesPartnerSearchQuery, hasPreferredSalesPartner: boolean): SalesPartnerSearchQuery => {
  const hasCoordinates = query.lat !== null && query.lng !== null;
  const isNonAddressSearch = query.searchType === SearchType.Partner || query.searchType === SearchType.CertificateID;
  const normalized: SalesPartnerSearchQuery = {
    ...query,
    productCompetence: query.productCompetence ?? [],
    certifiedCompetence: query.certifiedCompetence ?? [],
    countryCode: isNonAddressSearch ? "" : query.countryCode.toLowerCase(),
    lat: isNonAddressSearch ? null : hasCoordinates ? query.lat : null,
    lng: isNonAddressSearch ? null : hasCoordinates ? query.lng : null,
    preferredOnly: hasPreferredSalesPartner ? query.preferredOnly : false,
  };

  return normalized;
};

const selectedOptionFromQuery = (query: SalesPartnerSearchQuery): Suggestion | null => {
  if (query.query) {
    const isNonAddressSearch = query.searchType === SearchType.Partner || query.searchType === SearchType.CertificateID;
    return {
      label: query.query,
      group: isNonAddressSearch ? SearchType.Partner : query.searchType,
      searchType: query.searchType,
    };
  }

  return null;
};

const SalesPartnerFinderSearchBox = (props: Props) => {
  const { t } = useTranslation();
  const {
    query,
    markersLoading,
    onCommit,
    hasPreferredSalesPartner,
    certifiedCompetencesOptions,
    productCompetencesOptions,
    isCheckout,
  } = props;

  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [mobileDraft, setMobileDraft] = useState(query);
  const [addressSuggestions, setAddressSuggestions] = useState<Suggestion[]>([]);
  const [partnerSuggestions, setPartnerSuggestions] = useState<Suggestion[]>([]);
  const [selectedOption, setSelectedOption] = useState<Suggestion | null>(() => selectedOptionFromQuery(query));
  const [locationDenied, setLocationDenied] = useState(false);
  const [isFilterChanged, setIsFilterChanged] = useState(false);

  const groupedSelectRef = useRef<GroupedSelectRef>(null);
  const spFinderFiltersRef = useRef<SPFinderFiltersRef>(null);

  const screenSize = useNumericScreenSize();
  const isTablet = screenSize <= useNumericScreenSize.tablet;
  const isMobile = screenSize <= useNumericScreenSize.phone;
  const activeQuery = isMobile ? mobileDraft : query;

  const { fetchData: getAutocomplete } = useGoogleAutocomplete();

  useEffect(() => {
    if (!isMobile || !showFilterDialog) {
      setSelectedOption(selectedOptionFromQuery(query));
      setMobileDraft(query);
    }
  }, [
    isMobile,
    query.certifiedCompetence.join("|"),
    query.countryCode,
    query.lat,
    query.lng,
    query.preferredOnly,
    query.productCompetence.join("|"),
    query.query,
    query.radius,
    query.searchType,
    showFilterDialog,
  ]);

  const trackFilterApplied = (nextQuery: SalesPartnerSearchQuery, appliedFilter?: string) => {
    trackPartnerFinderSearch(
      buildSalesPartnerSearchTrackingValues(nextQuery),
      "Filter Applied",
      "Click",
      undefined,
      appliedFilter,
    );
  };

  const updateActiveQuery = (nextQuery: SalesPartnerSearchQuery, appliedFilter?: string) => {
    const normalizedQuery = normalizeQuery(nextQuery, hasPreferredSalesPartner);

    if (isMobile) {
      setMobileDraft(normalizedQuery);
      if (!showFilterDialog) {
        setShowFilterDialog(true);
      }
      return;
    }

    if (appliedFilter) {
      trackFilterApplied(normalizedQuery, appliedFilter);
    }
    setIsFilterChanged(true);
    onCommit(normalizedQuery);
  };

  const applyMobileDraft = () => {
    const normalizedDraft = normalizeQuery(mobileDraft, hasPreferredSalesPartner);

    if (normalizedDraft.preferredOnly && !query.preferredOnly) {
      trackSalesPartnerFinderSelectPreferred();
    }

    trackFilterApplied(normalizedDraft);
    setIsFilterChanged(true);
    onCommit(normalizedDraft);
    setShowFilterDialog(false);
  };

  const handleOpenFilterDialog = () => {
    setMobileDraft(query);
    setSelectedOption(selectedOptionFromQuery(query));
    setShowFilterDialog(true);
  };

  const handleCloseFilterDialog = () => {
    setMobileDraft(query);
    setSelectedOption(selectedOptionFromQuery(query));
    setShowFilterDialog(false);
  };

  const handleProductChange = (event: SelectChangeEvent<typeof activeQuery.productCompetence>) => {
    const {
      target: { value },
    } = event;
    const newProducts = typeof value === "string" ? value.split(",") : value;
    updateActiveQuery({ ...activeQuery, productCompetence: newProducts }, "Products");
  };

  const handleServiceChange = (event: SelectChangeEvent<typeof activeQuery.certifiedCompetence>) => {
    const {
      target: { value },
    } = event;
    const newServices = typeof value === "string" ? value.split(",") : value;
    updateActiveQuery({ ...activeQuery, certifiedCompetence: newServices }, "Services");
  };

  const getLabelByKey = (key: string, options: Option[]) => {
    return options.find((item) => item.key === key)?.label || key;
  };

  const onSearchInputChange = useCallback(
    throttle(async (newValue: string) => {
      if (newValue.length > 1) {
        const [predictions, res] = await Promise.all([
          getAutocomplete(newValue.trimLeft()),
          getCompanyList(newValue.trim()),
        ]);

        const addrSuggestions = predictions.map((p) => ({
          label: p.description,
          group: SearchType.Address,
          placeID: p.place_id,
        }));

        const partnerSuggs = res.autosuggestResults.map((result) => ({
          label: result.name,
          group: SearchType.Partner,
          searchType: result.isCertificateResult ? SearchType.CertificateID : SearchType.Partner,
          searchQuery: result.isCertificateResult ? result.certificateNr : result.name,
        }));

        setAddressSuggestions(addrSuggestions);
        setPartnerSuggestions(partnerSuggs);
      }
    }, 1000),
    [getAutocomplete],
  );

  const handlePreferredSalesPartnerChange = (newVal: boolean) => {
    if (!isMobile && newVal && !activeQuery.preferredOnly) {
      trackSalesPartnerFinderSelectPreferred();
    }
    updateActiveQuery({ ...activeQuery, preferredOnly: newVal }, "My Preffered sales partner");
  };

  const handleSelectedOptionChange = async (newOption: Suggestion | null) => {
    setSelectedOption(newOption);

    if (newOption == null) {
      updateActiveQuery({
        ...activeQuery,
        query: "",
        searchType: SearchType.Address,
        countryCode: "",
        lat: null,
        lng: null,
      });
      return;
    }

    const optionSearchType = newOption.searchType ?? newOption.group;

    if (optionSearchType === SearchType.Address) {
      const countryCode = newOption.placeID ? (await getCountryDetail(newOption.placeID))?.toLowerCase() ?? "" : "";
      updateActiveQuery({
        ...activeQuery,
        query: newOption.searchQuery ?? newOption.label,
        radius: countryCode ? -1 : activeQuery.radius,
        searchType: SearchType.Address,
        countryCode,
        lat: null,
        lng: null,
      });
      return;
    }

    updateActiveQuery({
      ...activeQuery,
      query: newOption.searchQuery ?? newOption.label,
      searchType: optionSearchType as SearchType,
      countryCode: "",
      lat: null,
      lng: null,
    });
  };

  const setSelectedOptionFromSelect: Dispatch<SetStateAction<Suggestion | null>> = (value) => {
    const nextValue = typeof value === "function" ? value(selectedOption) : value;
    void handleSelectedOptionChange(nextValue);
  };

  const handleGeoSearch = async () => {
    try {
      const res = await fetchCurrentLocation();
      if (!res) {
        return;
      }

      const newRadius = hasPreferredSalesPartner ? -1 : 100;
      const nextQuery = normalizeQuery(
        {
          ...activeQuery,
          query: res.fullAddress,
          radius: newRadius,
          searchType: SearchType.Address,
          countryCode: "",
          lat: res.lat,
          lng: res.lng,
        },
        hasPreferredSalesPartner,
      );

      setSelectedOption({ label: res.fullAddress, group: SearchType.Address });
      updateActiveQuery(nextQuery);
    } catch (e: unknown) {
      if (e instanceof GeolocationPositionError && e.code === GeolocationPositionError.PERMISSION_DENIED) {
        setLocationDenied(true);
      }
    }
  };

  const handleReset = () => {
    const clearedQuery = emptySearchQuery(activeQuery);
    setSelectedOption(null);

    if (isMobile) {
      setMobileDraft(clearedQuery);
      if (!showFilterDialog) {
        setShowFilterDialog(true);
      }
      return;
    }

    trackPartnerFinderSearch(buildSalesPartnerSearchTrackingValues(clearedQuery), "All Information Cleared", "Click");
    setIsFilterChanged(true);
    onCommit(clearedQuery);
  };

  const handleRadiusChange = (newRadius: number) => {
    updateActiveQuery({ ...activeQuery, radius: newRadius }, "Radius");
  };

  const sortedSuggestions = (data: Suggestion[]) =>
    data.slice().sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

  const suggestedCombinedList = [...sortedSuggestions(addressSuggestions), ...sortedSuggestions(partnerSuggestions)];

  const footer = (
    <div className={classes.footerContainer}>
      {isCheckout && !isFilterChanged && !isMobile && (
        <Alert severity={"info"} className={classes.filterInfo}>
          {t("partnerFinder.filter.alert")}
        </Alert>
      )}

      <div className={classes.actionContainer}>
        <Button
          data-test-id={"find-partner"}
          variant="text"
          disabled={markersLoading}
          onClick={handleReset}
          fullWidth={isMobile}
          startIcon={!isMobile && <Icon type={IconType.Reload} className={classes.resetIcon} />}
        >
          {t("searchPage.refinement.button.clearAll")}
        </Button>
        {isMobile && (
          <Button
            data-test-id={"find-partner"}
            onClick={applyMobileDraft}
            fullWidth={isMobile}
            startIcon={!isMobile && <SearchIcon />}
          >
            {isMobile ? t("searchPage.refinement.button.apply") : t("partnerFinder.search.findButton")}
          </Button>
        )}
      </div>
    </div>
  );

  const locationDeniedAlert = () => {
    if (locationDenied) {
      return <Alert severity={"info"}>{t("salesPartner.searchCard.geoLocationError")}</Alert>;
    }
    return null;
  };

  const filtersCheckbox = (
    <>
      <Grid size={isTablet ? 12 : 6}>
        <div className={classes.filterCountry}>
          <div className={classes.filterCheckbox}>
            <Button variant="text" startIcon={<LocationSearchingIcon />} onClick={handleGeoSearch}>
              {t("salesPartner.searchCard.geoLocation")}
            </Button>
            {showFilterDialog && isMobile && locationDeniedAlert()}
            <ChipSelect
              value={activeQuery.preferredOnly ? "preferred" : null}
              allowEmpty
              multiple={false}
              disabled={!hasPreferredSalesPartner}
              onChange={(_, newValue) => {
                handlePreferredSalesPartnerChange(newValue === "preferred");
              }}
            >
              <ChipSelect.Option value="preferred">
                {t("salesPartner.searchCard.showPreferredPartner")}
              </ChipSelect.Option>
            </ChipSelect>
          </div>
        </div>
      </Grid>
      {!isMobile && <Grid size={12}>{locationDeniedAlert()}</Grid>}
      <Grid size={12}>
        <SPFinderFilters
          ref={spFinderFiltersRef}
          certifiedCompetencesOptions={certifiedCompetencesOptions}
          productCompetencesOptions={productCompetencesOptions}
          handleProductChange={handleProductChange}
          handleServiceChange={handleServiceChange}
          getLabelByKey={getLabelByKey}
          selectedProductKeys={activeQuery.productCompetence}
          selectedServiceKeys={activeQuery.certifiedCompetence}
        />
      </Grid>
      {isCheckout && !isFilterChanged && isMobile && (
        <Alert severity={"info"} className={classes.filterInfo}>
          {t("partnerFinder.filter.alert")}
        </Alert>
      )}
    </>
  );

  const autoCompleteSearch = (showFilterButton: boolean) => (
    <GroupedSelect
      ref={groupedSelectRef}
      disabled={markersLoading}
      openFilterDialog={handleOpenFilterDialog}
      showFilterButton={showFilterButton}
      selectedOption={selectedOption}
      suggestedCombinedList={suggestedCombinedList}
      onSearchInputChange={onSearchInputChange}
      setSelectedOption={setSelectedOptionFromSelect}
      radiusValue={activeQuery.radius}
      onRadiusChange={handleRadiusChange}
    />
  );

  return (
    <>
      {showFilterDialog && isMobile && (
        <Modal
          title="Filter"
          onClose={handleCloseFilterDialog}
          footerChildren={footer}
          footerClassName={classes.filterFooter}
        >
          {autoCompleteSearch(false)}
          <RadiusSelectButtonGroup value={activeQuery.radius} onChange={handleRadiusChange} />
          {filtersCheckbox}
        </Modal>
      )}
      <Box className={classes.searchBox}>
        <Grid container spacing={2} alignItems={"center"}>
          <Grid size={isTablet ? 12 : 6}>{autoCompleteSearch(true)}</Grid>
          {!isMobile && filtersCheckbox}
          {!isMobile && <Grid size={12}>{footer}</Grid>}
        </Grid>
      </Box>
    </>
  );
};

export default memo(SalesPartnerFinderSearchBox);
